import { isEqualWith } from 'lodash';
import moment from 'moment';
import { store } from '../store';
import { wait } from '../utils';
import { type QSheetRecord, db } from './db';

type FromType = 'editor' | 'para';

interface EditorState {
  querySheets: Record<
    string,
    {
      catalog?: unknown;
      cluster?: unknown;
      database?: unknown;
      order_by?: number;
      uuid?: string;
      isFetched?: boolean;
      last_saved_at?: number;
    }
  >;
  queries: Record<string, { queryText?: string }>;
  qSheetList: string[];
  activeTab: number;
}

interface ParagraphState {
  querySheets: Record<
    string,
    {
      catalog?: unknown;
      cluster?: unknown;
      database?: unknown;
      order_by?: number;
      uuid?: string;
      isFetched?: boolean;
      last_saved_at?: number;
    }
  >;
  queries: Record<string, { queryText?: string }>;
  queriesState: Record<
    string,
    {
      para_index?: number;
      s3_key?: string;
      start_time?: number;
      name?: string;
    }
  >;
  qSheetList: string[];
  activeTab: number;
}

interface AuthState {
  data: { uuid?: string };
}

interface RootState {
  editor: EditorState;
  paragraph: ParagraphState;
  auth: AuthState;
}

const customIsEqual = (obj1: unknown, obj2: unknown, keysToIgnore: string[] = []): boolean => {
  return isEqualWith(obj1, obj2, (_val1, _val2, key) => {
    if (keysToIgnore.includes(key as string)) {
      return true; // Skip comparison for these keys
    }
    return undefined; // Use default comparison for other keys
  });
};

// Add a new item with error handling - editor only
export const addQSheet = async (
  tab_id: string,
  ref_id: string,
  recordTimestamp: boolean = true
): Promise<void> => {
  try {
    if (!tab_id || !ref_id) {
      throw new Error('param missing');
    }

    await wait(500); // wait for redux to update for safe side

    const payload = QuerySaveEditorPayload(tab_id, ref_id, recordTimestamp);
    if (!payload) {
      throw new Error('error in creating query-sheet payload : ' + tab_id + ' ' + ref_id);
    }
    await db.qSheets.add({ tab_id, ref_id, sheetData: payload, from: 'editor' }); // insert a new record
  } catch (error) {
    let msg = '';
    if ((error as Error)?.message === 'param missing') {
      msg = 'params are missing (tab_id or ref_id)';
    } else {
      msg = `Failed to Create the qSheets : ${error}`;
    }
    throw new Error(msg);
  }
};

export const getAllQSheets = async (from?: FromType): Promise<QSheetRecord[]> => {
  try {
    const filterFrom = from || 'editor';
    return await db.qSheets.filter((item) => item.from === filterFrom).toArray();
  } catch (error) {
    console.error(`IndexedDB: Error retrieving all items from qSheet:`, error);
    throw new Error('Failed to retrieve all items from qSheets');
  }
};

export async function getQSheet(
  tab_id: string,
  ref_id: string,
  from?: FromType
): Promise<QSheetRecord | undefined> {
  if (!tab_id || !ref_id) {
    return undefined;
  }
  try {
    const filterFrom = from || 'editor';
    return await db.qSheets
      .where('[tab_id+ref_id]')
      .equals([tab_id, ref_id])
      .filter((item) => item.from === filterFrom)
      .first();
  } catch (error) {
    console.error('Failed to fetch sheets by tab_id & ref_id :', error);
    throw new Error('Failed to retrieve items by tab_id & ref_id');
  }
}

// Get all editor sheets
export async function getAllEditorQSheets(): Promise<QSheetRecord[]> {
  try {
    return await db.qSheets.filter((item) => item.from === 'editor').toArray();
  } catch (error) {
    console.error('Failed to fetch editor sheets :', error);
    throw new Error('Failed to retrieve editor sheets');
  }
}

export async function getQSheetsBy_Tab(tab_id: string, from?: FromType): Promise<QSheetRecord[]> {
  if (!tab_id) {
    return [];
  }
  try {
    const filterFrom = from || 'editor';
    return await db.qSheets
      .where('tab_id')
      .equals(tab_id)
      .filter((item) => item.from === filterFrom)
      .toArray();
  } catch (error) {
    console.error('Failed to fetch entries by tab_id :', error);
    throw new Error('Failed to retrieve items by tab_id');
  }
}

export async function deleteQSheet(tab_id: string, ref_id: string): Promise<number> {
  try {
    if (!tab_id || !ref_id) {
      throw new Error('param missing');
    }
    const count = await db.qSheets
      .where('[tab_id+ref_id]')
      .equals([tab_id, ref_id])
      .filter((item) => item.from === 'editor')
      .delete();

    return count;
  } catch (error) {
    let msg = '';
    if ((error as Error)?.message === 'param missing') {
      msg = 'params are missing (tab_id or ref_id)';
    } else {
      msg = `Failed to delete item from qSheets : ${error}`;
    }
    throw new Error(msg);
  }
}

export async function deleteQSheetsBy_Tab(tab_id: string, from?: FromType): Promise<number> {
  try {
    if (!tab_id) {
      throw new Error('param missing');
    }
    const filterFrom = from || 'editor';
    const count = await db.qSheets
      .where('tab_id')
      .equals(tab_id)
      .filter((item) => item.from === filterFrom)
      .delete();
    return count;
  } catch (error) {
    let msg = '';
    if ((error as Error)?.message === 'param missing') {
      msg = 'params are missing (tab_id)';
    } else {
      msg = `Failed to delete item from qSheets : ${error}`;
    }
    throw new Error(msg);
  }
}

export const updateQSheet = async (
  tab_id: string,
  ref_id: string,
  recordTimestamp: boolean = true
): Promise<void> => {
  try {
    if (!tab_id || !ref_id) {
      throw new Error('param missing');
    }

    await wait(500); // wait for redux to update for safe side

    const updatedPayload = QuerySaveEditorPayload(tab_id, ref_id, recordTimestamp);
    if (!updatedPayload) {
      throw new Error('error in creating query-sheet payload');
    }
    const record = { tab_id, ref_id, sheetData: updatedPayload, from: 'editor' as const };
    const existingRecord = await getQSheet(tab_id, ref_id);
    if (existingRecord) {
      const areEqual = customIsEqual(existingRecord, record, [
        'last_modified_at',
        'last_saved_at',
        'meta_uuid',
      ]);

      if (!areEqual) {
        await db.qSheets
          .where('[tab_id+ref_id]')
          .equals([tab_id, ref_id])
          .filter((item) => item.from === 'editor')
          .modify((item) => {
            item.sheetData = record.sheetData;
          });
      }
    } else {
      throw new Error(`Record does not exist for : ${tab_id}__${ref_id}`);
    }
  } catch (error) {
    let msg = '';
    if ((error as Error)?.message === 'param missing') {
      msg = 'params are missing (tab_id or ref_id or from = "editor" or "para")';
    } else {
      msg = `Failed to upsert the qSheets : ${error}`;
    }
    throw new Error(msg);
  }
};

export const updateAllQSheetsSequence = async (from: FromType): Promise<void> => {
  try {
    if (!['editor', 'para'].includes(from)) {
      throw new Error(`param 'from' missing : 'editor' or 'para'`);
    }

    await wait(500); // wait for redux to update for safe side
    const state = store.getState() as unknown as RootState;
    let querySheetList: string[] = [];
    switch (from) {
      case 'editor':
        querySheetList = state.editor.qSheetList;
        querySheetList.forEach(async (tab_id, tabIndex) => {
          await db.qSheets
            .where('tab_id')
            .equals(tab_id)
            .filter((item) => item.from === from)
            .modify((item) => {
              item.sheetData.upload_data.order_by = tabIndex;
            });
        });
        break;
      case 'para':
        querySheetList = state.paragraph.qSheetList;
        querySheetList.forEach(async (tab_id, tabIndex) => {
          const existingData = await getQSheetsBy_Tab(tab_id, from);
          existingData?.forEach(async (existData) => {
            await db.qSheets
              .where('[tab_id+ref_id]')
              .equals([existData?.tab_id, existData?.ref_id])
              .filter((item) => item.from === from)
              .modify((item) => {
                item.sheetData.upload_data.order_by = tabIndex;
              });
          });
        });
        break;

      default:
        break;
    }
  } catch (error) {
    let msg = '';
    if ((error as Error)?.message === 'param missing') {
      msg = 'params are missing (tab_id or ref_id  or from = "editor" or "para")';
    } else {
      msg = `Failed to upsert the qSheets : ${error}`;
    }
    throw new Error(msg);
  }
};

// replace upsert with update function only ....2 browser tab deleted tab
export const upsertQSheet = async (
  tab_id: string,
  ref_id: string,
  from: FromType,
  recordTimestamp: boolean = true
): Promise<void> => {
  try {
    if (!tab_id || !ref_id || !['editor', 'para'].includes(from)) {
      throw new Error('param missing');
    }

    await wait(500); // wait for redux to update for safe side

    let payload: QSheetRecord['sheetData'] | null = null;
    switch (from) {
      case 'editor':
        payload = QuerySaveEditorPayload(tab_id, ref_id, recordTimestamp);
        break;

      case 'para':
        payload = QuerySaveParaPayload(tab_id, ref_id, recordTimestamp);
        break;

      default:
        break;
    }
    if (!payload) {
      throw new Error('error in upsert query-sheet payload');
    }
    const record: QSheetRecord = { tab_id, ref_id, sheetData: payload, from };
    const existingRecord = await getQSheet(tab_id, ref_id, from);
    if (existingRecord) {
      const areEqual = customIsEqual(existingRecord, record, [
        'last_modified_at',
        'last_saved_at',
        'meta_uuid',
      ]);
      if (!areEqual) {
        await db.qSheets
          .where('[tab_id+ref_id]')
          .equals([tab_id, ref_id])
          .filter((item) => item.from === 'editor')
          .modify((item) => {
            item.sheetData = record.sheetData;
          });
      }
    } else {
      await db.qSheets.add(record); // If it doesn't exist, insert a new record
    }
  } catch (error) {
    let msg = '';
    if ((error as Error)?.message === 'param missing') {
      msg = 'params are missing (tab_id or ref_id or from = "editor" or "para")';
    } else {
      msg = `Failed to upsert the qSheets : ${error}`;
    }
    throw new Error(msg);
  }
};

export const clearQSheetsTable = async (tableName?: string): Promise<void> => {
  try {
    await db.qSheets.clear();
  } catch (error) {
    console.error(`IndexedDB: Error clearing table ${tableName}:`, error);
    throw new Error('Failed to clear table');
  }
};

export const updateQsheetParaIndexesOfParagraphSheet = async (
  tab_id: string,
  ref_id: string,
  new_para_index: number,
  from: FromType
): Promise<void> => {
  try {
    if (
      !tab_id ||
      !ref_id ||
      (new_para_index !== 0 && !new_para_index) ||
      !['para'].includes(from)
    ) {
      throw new Error('param missing : this is avaialable for paragraph sheets only');
    }
    await db.qSheets
      .where('[tab_id+ref_id]')
      .equals([tab_id, ref_id])
      .filter((item) => item.from === from)
      .modify((item) => {
        item.sheetData.upload_data.last_modified_at = moment().unix();
        const queries = item.sheetData.upload_data.queries;
        if (!Array.isArray(queries)) {
          queries.para_index = new_para_index;
        }
      });
  } catch (error) {
    let msg = '';
    if (
      (error as Error)?.message === 'param missing : this is avaialable for paragraph sheets only'
    ) {
      msg = 'params are missing (tab_id or ref_id or new_para_index or from = "para")';
    } else {
      msg = `Failed to update para_index in the para qSheets : ${error}`;
    }
    throw new Error(msg);
  }
};

// ------------------ Helper Functions --------------------

function QuerySaveEditorPayload(
  tab_id: string,
  ref_id: string,
  recordTimestamp: boolean = true
): QSheetRecord['sheetData'] | null {
  const state = store.getState() as unknown as RootState;
  const { querySheets, queries, qSheetList, activeTab } = state.editor;
  const resolvedTabId = tab_id || qSheetList?.[activeTab] || '';
  const queryData = querySheets?.[resolvedTabId];
  const {
    catalog,
    cluster,
    database,
    order_by,
    uuid,
    isFetched,
    last_saved_at = 1,
  } = queryData || {};
  const { queryText } = queries?.[ref_id] || {};
  const upload_data: QSheetRecord['sheetData']['upload_data'] = {
    last_saved_at: last_saved_at,
    catalog,
    cluster,
    database,
    order_by,
    uuid,
    queries: { ref_id, query: queryText },
  };

  // Simplified validation without auth check
  if (!queryData?.uuid || !isFetched) {
    console.error('QuerySaveEditorPayload validation failed:', {
      hasQueryDataUuid: !!queryData?.uuid,
      isFetched,
      tab_id,
      ref_id,
    });
    return null;
  }
  const payload: QSheetRecord['sheetData'] = {
    group_uuid: resolvedTabId,
    upload_data,
  };
  if (recordTimestamp) {
    payload.upload_data.last_modified_at = moment().unix();
  } else if (!recordTimestamp && payload.upload_data.last_modified_at) {
    delete payload.upload_data.last_modified_at;
  }

  return payload;
}

function QuerySaveParaPayload(
  tab_id: string,
  ref_id: string,
  recordTimestamp: boolean = true
): QSheetRecord['sheetData'] | null {
  const state = store.getState() as unknown as RootState;
  const { querySheets, qSheetList, activeTab, ...restParaState } = state.paragraph;
  const resolvedTabId = tab_id || qSheetList?.[activeTab] || '';
  const sheetData = querySheets?.[resolvedTabId];
  const {
    catalog,
    cluster,
    database,
    order_by,
    uuid,
    isFetched,
    last_saved_at = 1,
  } = sheetData || {};
  const { data } = state.auth;

  if (!data.uuid || !sheetData?.uuid || !isFetched || ref_id.includes('previewEditor')) {
    return null;
  }

  const uploadingQueries = {
    query: restParaState?.queries[ref_id]?.queryText || '',
    para_index: restParaState.queriesState[ref_id]?.para_index || 0,
    uuid: ref_id,
    ref_id: ref_id,
    s3_key: restParaState?.queriesState[ref_id]?.s3_key,
    start_time: restParaState?.queriesState[ref_id]?.start_time,
    name: restParaState?.queriesState[ref_id]?.name,
  };

  const payload: QSheetRecord['sheetData'] = {
    group_uuid: resolvedTabId,
    paragraph: true,
    upload_data: {
      last_saved_at: last_saved_at,
      cluster: cluster,
      database: database || {},
      catalog: catalog,
      queries: uploadingQueries,
      order_by,
      uuid,
    },
  };

  if (recordTimestamp) {
    payload.upload_data.last_modified_at = moment().unix();
  } else if (!recordTimestamp && payload.upload_data.last_modified_at) {
    delete payload.upload_data.last_modified_at;
  }

  return payload;
}

//  here we delete the unused querysheetd
export async function DeleteUnusedQSheetsBy_Tabs(
  serverExistingTabIds: Set<string>,
  from: FromType
): Promise<void> {
  try {
    if (!serverExistingTabIds || !['editor', 'para'].includes(from)) {
      throw new Error('param missing');
    }

    await wait(500); // wait for redux to update for safe side

    const existingTabs = await getAllQSheets(from);
    const tabIds = existingTabs?.map((item) => item.tab_id);
    const uniqueTabIds = [...new Set(tabIds)];
    const tabsToBeDeleted = uniqueTabIds.filter((x) => !serverExistingTabIds.has(x));

    tabsToBeDeleted?.forEach(async (tabId) => {
      await deleteQSheetsBy_Tab(tabId, from);
    });
  } catch (error) {
    let msg = '';
    if ((error as Error)?.message === 'param missing') {
      msg = 'params are missing (serverExistingTabIds from = "editor" or "para")';
    } else {
      msg = `Failed to upsert the qSheets : ${error}`;
    }
    throw new Error(msg);
  }
}

// here we only update the last saved at

export const updateLastSavedAt = async (
  tab_id: string,
  ref_id: string,
  from: FromType,
  savedAt: number
): Promise<void> => {
  try {
    if (!tab_id || !ref_id || !['editor', 'para'].includes(from)) {
      throw new Error('param missing');
    }

    await wait(500); // wait for redux to update for safe side

    const existingRecord = await getQSheet(tab_id, ref_id, from);
    if (existingRecord) {
      const updatedSheetData = {
        ...existingRecord,
        sheetData: {
          ...existingRecord?.sheetData,
          upload_data: {
            ...existingRecord?.sheetData?.upload_data,
            last_saved_at: savedAt,
          },
        },
      };
      await db.qSheets
        .where('[tab_id+ref_id]')
        .equals([tab_id, ref_id])
        .filter((item) => item.from === from)
        .modify((item) => {
          item.sheetData = updatedSheetData.sheetData;
        });
    } else {
      throw new Error(`Record does not exist for : ${tab_id}__${ref_id}`);
    }
  } catch (error) {
    let msg = '';
    if ((error as Error)?.message === 'param missing') {
      msg = 'params are missing (tab_id or ref_id or from = "editor" or "para")';
    } else {
      msg = `Failed to upsert the qSheets : ${error}`;
    }
    throw new Error(msg);
  }
};
